import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import { applyServerValidationErrors } from '@/utils/apiValidationErrors';

export interface CourseForm {
    course_name: string;
    description: string;
    is_active: boolean;
}

export interface CreateCourseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: (data: CourseForm) => void | Promise<void>;
    isSubmitting?: boolean;
}

const CreateCourseModal = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
}: CreateCourseModalProps) => {
    const { t } = useTranslation('courses');
    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<CourseForm>({
        defaultValues: {
            course_name: '',
            description: '',
            is_active: true,
        },
    });

    useEffect(() => {
        if (!isOpen) {
            reset({
                course_name: '',
                description: '',
                is_active: true,
            });
            return;
        }
    }, [isOpen, reset]);

    const handleClose = () => {
        clearErrors();
        reset({
            course_name: '',
            description: '',
            is_active: true,
        });
        onClose();
    };

    const handleFormSubmit = async (data: CourseForm) => {
        clearErrors();
        try {
            await Promise.resolve(onSubmit?.(data));
            reset({
                course_name: '',
                description: '',
                is_active: true,
            });
        } catch (error) {
            applyServerValidationErrors<CourseForm>(error, setError);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={t('modal.create_title')}>
            <form
                id="create-course-form"
                onSubmit={handleSubmit(handleFormSubmit)}
                noValidate
                className="overflow-y-auto p-4 2xl:p-5 flex flex-col gap-4"
            >
                <Input
                    label={t('modal.program_name_label')}
                    placeholder={t('modal.program_name_placeholder')}
                    required
                    registration={register('course_name', {
                        required: t('modal.validation.name_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.name_empty'),
                    })}
                    error={errors.course_name?.message}
                />

                <Textarea
                    label={t('modal.description_label')}
                    placeholder={t('modal.description_placeholder')}
                    rows={4}
                    required
                    registration={register('description', {
                        required: t('modal.validation.desc_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.desc_empty'),
                    })}
                    error={errors.description?.message}
                />
            </form>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={handleClose} disabled={isSubmitting}>
                    {t('modal.cancel_button')}
                </Button>

                <Button type="submit" variant="primary" form="create-course-form" disabled={isSubmitting}>
                    {isSubmitting ? t('modal.creating_button') : t('modal.create_button')}
                </Button>
            </div>
        </Modal>
    );
};

export default CreateCourseModal;
